import { useCallback, useEffect, useRef, useState } from 'react';
import { config as coreAnimationConfig } from '../CoreAnimation/conf';
import styles from './index.module.scss';
import Item1 from './item1';
import { useLang } from '@/i18n';

// 图片配置
// const TOTAL_FRAMES = 139;
// const BASE_URL = 'https://websiteoss.ecoflow.com/figma-uploads/1758624859771/';

const PRODUCT_IMAGE_URL = 'https://websiteoss.ecoflow.com/upload/image/1758264227361/2%E5%BA%A6%E7%94%B5_00040.png';

interface ImageCache {
    [key: number]: HTMLImageElement;
}

interface NewRefineProps {
    currentFrame: number;
    pageType: 'max' | 'ultra' | 'classic';
    loadingStep?: number; // 插空加载的步长，默认为2
}

const NewRefine: React.FC<NewRefineProps> = (
    { currentFrame, pageType, loadingStep = 2 }: NewRefineProps = { currentFrame: 0, pageType: 'max', loadingStep: 2 },
) => {
    const { t } = useLang('Redfine');

    const canvasRef = useRef<HTMLCanvasElement>(null);
    const imageCache = useRef<ImageCache>({});
    const [_imagesLoaded, setImagesLoaded] = useState(false);
    const [firstImageLoaded, setFirstImageLoaded] = useState(false);
    const [productImageLoaded, setProductImageLoaded] = useState(false);
    const productImage = useRef<HTMLImageElement | null>(null);
    const containerRef = useRef<HTMLDivElement>(null);
    const [_loadedFrames, setLoadedFrames] = useState<Set<number>>(new Set());
    const [keyFramesLoaded, setKeyFramesLoaded] = useState(false);
    const currentFrameRef = useRef<number>(currentFrame);

    const config = coreAnimationConfig[pageType];

    // 同步currentFrame到ref
    useEffect(() => {
        currentFrameRef.current = currentFrame;
    }, [currentFrame]);

    // 获取最近的已加载帧
    const getNearestLoadedFrame = useCallback(
        (targetFrame: number): number | null => {
            if (imageCache.current[targetFrame]) {
                return targetFrame;
            }

            // 向前查找最近的已加载帧
            for (let i = targetFrame - 1; i >= 1; i--) {
                if (imageCache.current[i]) {
                    return i;
                }
            }

            // 向后查找最近的已加载帧
            for (let i = targetFrame + 1; i <= config.totalFrames; i++) {
                if (imageCache.current[i]) {
                    return i;
                }
            }

            return null;
        },
        [config.totalFrames],
    );

    // Canvas绘制函数 - 只绘制视频帧
    const drawFrame = useCallback(
        (frameNumber: number) => {
            // console.log('drawFrame', frameNumber);

            const canvas = canvasRef.current;
            const container = containerRef.current;

            // 尝试获取目标帧，如果没有则获取最近的已加载帧
            const actualFrame = getNearestLoadedFrame(frameNumber);
            const image = actualFrame ? imageCache.current[actualFrame] : null;

            if (!canvas || !container || !image) {
                console.log('drawFrame failed - missing elements:', { canvas: !!canvas, container: !!container, image: !!image, actualFrame });
                return;
            }

            const ctx = canvas.getContext('2d');
            if (!ctx) {
                console.log('drawFrame failed - no context');
                return;
            }

            // 获取容器的显示尺寸（不考虑DPR）
            const rect = container.getBoundingClientRect();
            const displayWidth = rect.width;
            const displayHeight = rect.height;

            // 如果容器尺寸为0，说明还没有正确渲染，延迟重试
            if (displayWidth === 0 || displayHeight === 0) {
                console.log('Container has zero dimensions, retrying...');
                setTimeout(() => drawFrame(frameNumber), 100);
                return;
            }

            // 获取图片尺寸
            const imgWidth = image.naturalWidth;
            const imgHeight = image.naturalHeight;

            // 计算cover效果的缩放比例（基于显示尺寸）
            const displayRatio = displayWidth / displayHeight;
            const imgRatio = imgWidth / imgHeight;

            let drawWidth, drawHeight, offsetX, offsetY;

            if (imgRatio > displayRatio) {
                // 图片更宽，以高度为准
                drawHeight = displayHeight;
                drawWidth = imgWidth * (displayHeight / imgHeight);
                offsetX = (displayWidth - drawWidth) / 2;
                offsetY = 0;
            } else {
                // 图片更高，以宽度为准
                drawWidth = displayWidth;
                drawHeight = imgHeight * (displayWidth / imgWidth);
                offsetX = 0;
                offsetY = (displayHeight - drawHeight) / 2;
            }

            // 清除canvas并绘制图片
            ctx.clearRect(0, 0, canvas.width, canvas.height);
            ctx.drawImage(image, offsetX, offsetY, drawWidth, drawHeight);

            currentFrameRef.current = frameNumber;
        },
        [getNearestLoadedFrame],
    );

    // 生成图片URL
    const getImageUrl = useCallback(
        (frameNumber: number) => {
            if (window.innerWidth < 768) {
                // https://websiteoss.ecoflow.com/figma-uploads/m-video-frame-1.webp
                return `${config.baseUrl}frame-${frameNumber}.webp`;
            }

            if (frameNumber >= config.splitPngCount && frameNumber <= config.totalFrames) {
                return config.pngImages[frameNumber - config.splitPngCount];
            }

            return `${config.baseUrl}frame-${frameNumber}.webp`;
        },
        [config.baseUrl, config.pngImages, config.splitPngCount, config.totalFrames],
    );

    // 调整canvas尺寸
    const resizeCanvas = useCallback(() => {
        const canvas = canvasRef.current;
        const container = containerRef.current;

        if (!canvas || !container) return;

        // 获取容器尺寸
        const rect = container.getBoundingClientRect();
        const dpr = window.devicePixelRatio || 1;

        // 设置canvas实际尺寸
        canvas.width = rect.width * dpr;
        canvas.height = rect.height * dpr;

        // 设置canvas显示尺寸
        canvas.style.width = `${rect.width}px`;
        canvas.style.height = `${rect.height}px`;

        // 缩放上下文以匹配设备像素比
        const ctx = canvas.getContext('2d');
        if (ctx) {
            ctx.scale(dpr, dpr);
        }

        // 重新绘制当前帧
        if (firstImageLoaded && currentFrameRef.current > 0) {
            drawFrame(currentFrameRef.current);
        } else if (firstImageLoaded) {
            // 如果没有当前帧，显示第一帧
            drawFrame(1);
        }
    }, [firstImageLoaded, drawFrame]);

    // 优先加载第一张图片
    const loadFirstImage = useCallback(async () => {
        console.log('Starting to load first image...');
        const img = new Image();
        img.crossOrigin = 'anonymous';
        img.onload = () => {
            console.log('First image loaded successfully');
            imageCache.current[1] = img;
            setFirstImageLoaded(true);
            setLoadedFrames((prev) => new Set(Array.from(prev).concat([1])));
            // 确保canvas已经正确设置尺寸后再绘制
            setTimeout(() => {
                // 先调整canvas尺寸
                resizeCanvas();
                // 然后绘制第一张图片
                drawFrame(1);
            }, 150);
        };
        img.onerror = (error) => {
            console.error('Failed to load first frame:', error);
        };
        const imageUrl = getImageUrl(1);
        console.log('Loading image from URL:', imageUrl);
        img.src = imageUrl;
    }, [getImageUrl, drawFrame, resizeCanvas]);

    // 加载产品图片
    const loadProductImage = useCallback(async () => {
        const img = new Image();
        img.crossOrigin = 'anonymous';
        img.onload = () => {
            productImage.current = img;
            setProductImageLoaded(true);
            console.log('Product image loaded');
        };
        img.onerror = () => {
            console.warn('Failed to load product image');
        };
        img.src = PRODUCT_IMAGE_URL;
    }, []);
    // 加载单个帧的函数
    const loadSingleFrame = useCallback(
        (frameNumber: number): Promise<void> => {
            return new Promise<void>((resolve, reject) => {
                // 如果已经加载过，直接返回
                if (imageCache.current[frameNumber]) {
                    resolve();
                    return;
                }

                const img = new Image();
                img.crossOrigin = 'anonymous';
                img.onload = () => {
                    imageCache.current[frameNumber] = img;
                    setLoadedFrames((prev) => new Set(Array.from(prev).concat([frameNumber])));
                    // console.log(`Frame ${frameNumber} loaded`);
                    resolve();
                };
                img.onerror = () => {
                    console.warn(`Failed to load frame ${frameNumber}`);
                    reject(new Error(`Failed to load frame ${frameNumber}`));
                };
                img.src = getImageUrl(frameNumber);
            });
        },
        [getImageUrl],
    );

    // 插空预加载图片 - 先加载关键帧，然后填充中间帧
    const preloadImages = useCallback(async () => {
        console.log('开始插空预加载图片...');

        // 如果第一张图片还没加载，先加载它
        if (!firstImageLoaded) {
            await loadFirstImage();
        }

        // 第一阶段：加载关键帧（按步长插空加载）
        console.log(`加载关键帧，步长为 ${loadingStep}`);
        const keyFramePromises: Promise<void>[] = [];

        for (let i = 1; i <= config.totalFrames; i += loadingStep) {
            if (i !== 1) {
                // 第一帧已经加载
                keyFramePromises.push(
                    loadSingleFrame(i).catch((error) => {
                        console.warn(`关键帧 ${i} 加载失败:`, error);
                    }),
                );
            }
        }

        try {
            await Promise.all(keyFramePromises);
            setKeyFramesLoaded(true);
            console.log('关键帧加载完成');
        } catch (error) {
            console.error('关键帧加载出错:', error);
            setKeyFramesLoaded(true);
        }

        // 第二阶段：填充中间帧（后台异步加载）
        console.log('开始填充中间帧...');
        const fillFramePromises: Promise<void>[] = [];

        for (let i = 2; i <= config.totalFrames; i++) {
            // 跳过已经加载的关键帧
            if (i % loadingStep !== 1) {
                fillFramePromises.push(
                    loadSingleFrame(i).catch((error) => {
                        console.warn(`中间帧 ${i} 加载失败:`, error);
                    }),
                );
            }
        }

        // 异步加载中间帧，不阻塞播放
        Promise.all(fillFramePromises)
            .then(() => {
                setImagesLoaded(true);
                console.log('所有图片预加载完成');
            })
            .catch((error) => {
                console.error('中间帧加载出错:', error);
                setImagesLoaded(true);
            });
    }, [firstImageLoaded, loadFirstImage, loadSingleFrame, loadingStep, config.totalFrames]);

    const [isMobile, setIsMobile] = useState(true);
    // 页面加载完毕后立即开始预加载
    useEffect(() => {
        if (isMobile) {
            return;
        }
        const initializeComponent = () => {
            console.log('初始化组件...');

            // 检查DOM元素是否准备好
            if (!canvasRef.current || !containerRef.current) {
                console.log('DOM elements not ready, retrying...');
                setTimeout(initializeComponent, 100);
                return;
            }

            console.log('DOM elements ready, initializing canvas and loading first image');

            // 初始化canvas尺寸
            resizeCanvas();

            // 加载产品图片
            if (!productImageLoaded) {
                loadProductImage();
            }

            // 立即开始加载第一张图片
            if (!firstImageLoaded) {
                loadFirstImage();
            }

            // 延迟一点时间后开始预加载所有图片，避免阻塞首屏渲染
            if (!keyFramesLoaded && Object.keys(imageCache.current).length <= 1) {
                setTimeout(() => {
                    preloadImages();
                }, 500);
            }
        };

        const timer = setTimeout(initializeComponent, 200); // 延迟确保DOM已渲染
        return () => clearTimeout(timer);
    }, [firstImageLoaded, keyFramesLoaded, loadFirstImage, preloadImages, resizeCanvas, loadProductImage, productImageLoaded, isMobile]);

    useEffect(() => {
        if (isMobile) {
            return;
        }
        // 只要有第一帧加载完成，就可以开始播放
        if (firstImageLoaded && currentFrame > 0) {
            drawFrame(currentFrame);
        } else if (firstImageLoaded) {
            drawFrame(1);
        }
    }, [currentFrame, drawFrame, firstImageLoaded, isMobile]);

    useEffect(() => {
        setIsMobile(window.innerWidth < 1024);
    }, []);

    return (
        <div className={styles.NewRefine + ' GtmModuleData-section'} id="NewRefine" data-section-id="d3m-new-refine">
            <div className="container-wrapper" ref={containerRef}>
                <canvas ref={canvasRef} className={'canvas'} />
                {/* <img src={PRODUCT_IMAGE_URL} alt="Product" className="product-image" /> */}
                <div id="textWrapper" className={`text-wrapper ${pageType}`}>
                    {true &&
                        t.rich('title', {
                            sup: (text: string) => <sup>{text}</sup>,
                        })}
                </div>

                <Item1 pageType={pageType} />
            </div>
        </div>
    );
};

export default NewRefine;
